import { ReactNode } from "react";
import { alpha, Box, styled, Typography } from "@mui/material";
import IotVisionToolTips from "components/common/IotVisionToolTips";
import { LoadingWrapper } from "components/wrappers/LoadingWrapper";

const BORDER_COLOR = "#E2E5EC";
const TEXT_COLOR = "#21315B";

type KpiAccentColor = "error" | "warning" | "success" | "primary";

const TINTED_BACKGROUND_ACCENTS: KpiAccentColor[] = ["error", "warning"];

const KpiCardContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "accentColor",
})<{ accentColor?: KpiAccentColor }>(({ theme, accentColor }) => {
    const isTinted = accentColor && TINTED_BACKGROUND_ACCENTS.includes(accentColor);
    const accentMain = accentColor ? theme.palette[accentColor].main : undefined;

    return {
        background: isTinted
            ? `linear-gradient(180deg, ${alpha(accentMain as string, 0.12)} 0%, ${alpha(
                  accentMain as string,
                  0,
              )} 40%)`
            : theme.palette.common.white,
        border: `1px solid ${isTinted ? accentMain : BORDER_COLOR}`,
        padding: "15px 23px",
        borderRadius: "10px",
        textAlign: "left",
        color: theme.palette.primary.main,
        display: "flex",
        flexDirection: "column",
        width: "100%",
    };
});

const IconBadge = styled(Box, {
    shouldForwardProp: (prop) => prop !== "accentColor",
})<{ accentColor: KpiAccentColor }>(({ theme, accentColor }) => ({
    width: 43,
    height: 43,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: alpha(theme.palette[accentColor].main, 0.12),
    color: theme.palette[accentColor].main,
}));

interface IKpiCardProps {
    title: string;
    value: string | number;
    valueSuffix?: string;
    tooltip?: string;
    isLoading?: boolean;
    icon?: ReactNode;
    accentColor?: KpiAccentColor;
    description?: string;
}

const KpiCard = ({
    title,
    value,
    valueSuffix,
    tooltip,
    isLoading = false,
    icon,
    accentColor,
    description,
}: IKpiCardProps): JSX.Element => {
    const titleNode = (
        <Typography
            variant="body2"
            fontWeight={600}
            sx={{
                color: TEXT_COLOR,
                width: "fit-content",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                fontSize: "12px",
            }}
        >
            {title}
        </Typography>
    );

    return (
        <KpiCardContainer accentColor={accentColor}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                {tooltip ? (
                    <IotVisionToolTips title={tooltip} arrow>
                        {titleNode}
                    </IotVisionToolTips>
                ) : (
                    titleNode
                )}
                {icon && accentColor && <IconBadge accentColor={accentColor}>{icon}</IconBadge>}
            </Box>
            <LoadingWrapper isLoading={isLoading} sx={{ mt: "15px" }}>
                <Typography variant="h4" fontWeight={600} sx={{ color: TEXT_COLOR, mt: "15px" }} component="div">
                    {value}
                    {valueSuffix && (
                        <Typography
                            component="span"
                            variant="body2"
                            fontWeight={400}
                            sx={{ color: "text.secondary", ml: "6px" }}
                        >
                            {valueSuffix}
                        </Typography>
                    )}
                </Typography>
                {description && (
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: "6px" }}>
                        {description}
                    </Typography>
                )}
            </LoadingWrapper>
        </KpiCardContainer>
    );
};

export default KpiCard;
